import React from 'react';
import { motion } from 'framer-motion';
import { useTransition } from './useTransition';
import { TransitionEvents, TransitionProps } from './Transition.model';
import { pickDefined } from '../utils';

export type { TransitionProps };

/**
 * Transition component
 *
 * Renders an element with a transition animation (fade + slide).
 *
 * @example
 * ```tsx
 * <Transition 
 *   yOffset={20}
 *   duration={0.3}
 *   direction="forward"
 * >
 *   <div>Content with transition</div>
 * </Transition>
 * ```
 */
export const Transition = React.forwardRef<HTMLDivElement, TransitionProps>(
  (
    {
      children,
      className,
      id,
      as = 'div',
      motionProps: additionalMotionProps,
      yOffset,
      direction,
      duration,
      enabled,
      animateHeight,
      onEnterStart,
      onEnterComplete,
      onExitStart,
      onExitComplete,
    },
    ref
  ) => {
    const config = pickDefined({
      yOffset,
      direction,
      duration,
      enabled,
      animateHeight,
    });

    const events: TransitionEvents = {
      onEnterStart,
      onEnterComplete,
      onExitStart,
      onExitComplete,
    };

    const { motionProps } = useTransition(config, events);

    const MotionComponent = motion(as);

    const finalMotionProps = {
      ...motionProps,
      ...additionalMotionProps,
      ref,
      className,
      id,
    };

    return <MotionComponent {...finalMotionProps}>{children}</MotionComponent>;
  }
);

Transition.displayName = 'Transition';
